/*
 *  -----------------------------------------------------------------------
 *    <copyright file="assessments-dialog/index.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DialogContent } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import { Checkbox } from '@progress/kendo-react-inputs';
import cn from 'clsx';
import AddRules from '@components/admin/add-rules';
import { ADD_RULES_FIELD_TYPES } from '@components/admin/add-rules/add-rules.data';
import { SVTRule } from '@components/admin/add-rules/rules-row/rules-row.interface';
import CopyButton from '@components/core/copy-button';
import CustomAutocomplete from '@components/core/custom-autocomplete';
import StepsBox from '@components/core/steps-box';
import { getInitialRules } from '../add-crf-dialog/add-crf-dialog.data';
import { useBoolean, useLocalState, useTranslation } from 'hooks';
import { StudyCrfFields } from 'queries/generated/fragments.generated';
import { studyCrfAssessments_OpraQueries_crfAssessments_StudyAssessment } from 'queries/generated/study-crfs.generated';
import { GET_STUDY_CRF_ASSESSMENTS, GET_STUDY_CRFS, SAVE_STUDY_CRF_ASSESSMENT } from 'queries/study-crfs';
import { checkConfigIdErrors, checkIfNameExist, checkRequiredFields, validateFields } from 'utils/validation';
import Button from 'views/components/core/buttons';
import CharactersRemaining from 'views/components/core/characters-remaining';
import { CustomDialog, DialogActions, DialogPage, DialogTitle } from 'views/components/core/dialog';
import ModalUpdatedInfo from 'views/components/core/modal-updated-info';
import TextField from 'views/components/core/text-field';
import Constants from '@utils/constants';
import { GET_STUDY } from '@queries/studies';

import './styles.scss';

interface AssessmentDialogInterface {
    isSysAdmin: boolean;
    close(action: string | undefined): any;
    currentAssessment?: studyCrfAssessments_OpraQueries_crfAssessments_StudyAssessment | null;
    assessments?: studyCrfAssessments_OpraQueries_crfAssessments_StudyAssessment[];
}

interface AssessmentValuesInterface {
    id: string | null;
    configId: string | null;
    active: boolean | null;
    name: string | null;
    crfId: string | undefined;
    endPoint: boolean | null;
    safety: boolean | null;
    lab: boolean | null;
    sendToVendor: boolean | null;
}

const AssessmentDialog = ({ isSysAdmin, close, currentAssessment = null, assessments }: AssessmentDialogInterface) => {
    const { t } = useTranslation();
    const { state: localState } = useLocalState();
    const studyId = localState.selectedStudy?.id;
    const initialAssessmentValues: AssessmentValuesInterface = {
        configId: null,
        active: true,
        name: '',
        crfId: '',
        endPoint: false,
        safety: false,
        lab: false,
        sendToVendor: false,
    };
    const initialErrors = { name: '', crfId: '' };
    const [assessmentValues, setAssessmentValues] = useState<AssessmentValuesInterface>({ ...initialAssessmentValues });
    const [errors, setErrors] = useState({ ...initialErrors } as { [key: string]: string });
    const [crfFormIdList, setCrfFormIdList] = useState<string[]>([]);
    const disableBtns = useBoolean(!currentAssessment);
    const isFormIdRequired = useBoolean(false);
    const [saveAssessment] = useMutation(SAVE_STUDY_CRF_ASSESSMENT);
    const { data: crfData } = useQuery(GET_STUDY_CRFS, { variables: { studyId } });
    const { data: actualStudy } = useQuery(GET_STUDY, { variables: { id: studyId } });
    const [addAssessmentState, setAddAssessmentState] = useState<SVTRule[]>(
        getInitialRules(currentAssessment?.assessmentRules),
    );
    const tabNumber = {
        step1Assessments: 1,
        step2Rules: 2,
    };
    const tabTitles = [t('admin:assessment1'), t('admin:rules2')];
    const [tab, setTab] = useState({
        number: tabNumber.step1Assessments,
        available: tabNumber.step1Assessments,
    } as DialogPage);
    const showRulesValidation = useBoolean(false);
    const isBackBtnDisabled = !!addAssessmentState.find((item) => !item.isValid);
    const isForcedValidation = !!addAssessmentState.find((item) => item.forceValidation);

    useEffect(() => {
        if (actualStudy?.study) isFormIdRequired.setValue(actualStudy.study.assessmentAssociation);
    }, [actualStudy]);

    const getRequiredFields = () => {
        const requiredAssessmentFields = ['name'];
        if (isFormIdRequired.value) {
            requiredAssessmentFields.push('crfId');
        }
        if (currentAssessment) {
            requiredAssessmentFields.push('configId');
        }

        return requiredAssessmentFields;
    };

    const crfConvert = (field: 'subjectCodes' | 'singleSubjectCode' | 'crfId', value?: string) => {
        if (field === 'subjectCodes') {
            const activeSubjectCodes: string[] = [];
            crfData?.crfs.forEach((crf: StudyCrfFields) => {
                if (crf.active) {
                    activeSubjectCodes.push(crf.subjectCode as string);
                }
            });
            return activeSubjectCodes;
        }

        if (field === 'crfId' && value) {
            const foundCrf = crfData?.crfs.find((crf: StudyCrfFields) => crf.subjectCode === value);
            return foundCrf?.id || Constants.ObjectId.Empty;
        }

        if (field === 'singleSubjectCode' && value) {
            const foundCrf = crfData?.crfs.find((crf: StudyCrfFields) => crf.id === value);
            return foundCrf?.subjectCode || '';
        }
    };

    useEffect(() => {
        if (!!crfData?.crfs.length) {
            const crfIdsList = crfConvert('subjectCodes');
            setCrfFormIdList([...crfIdsList]);
        }
    }, [crfData]);

    useEffect(() => {
        if (currentAssessment) {
            setAssessmentValues({
                id: currentAssessment.id,
                configId: currentAssessment.configId,
                active: currentAssessment.active,
                name: currentAssessment.name,
                crfId: currentAssessment.crfId !== Constants.ObjectId.Empty ? currentAssessment.crfId : '',
                endPoint: currentAssessment.endPoint,
                safety: currentAssessment.safety,
                lab: currentAssessment.lab,
                sendToVendor: currentAssessment.sendToVendor,
            });

            setTab({
                number: tabNumber.step1Assessments,
                available: tabNumber.step2Rules,
            });
        } else setAssessmentValues({ ...initialAssessmentValues });
    }, [currentAssessment, crfData]);

    const onClose = (action?: string) => {
        if (action !== 'error') {
            setAssessmentValues({ ...initialAssessmentValues });
            setErrors({ ...initialErrors });
        }
        disableBtns.setFalse();
        close(action);
        setTab({
            number: tabNumber.step1Assessments,
            available: tabNumber.step1Assessments,
        });
    };

    const validate = (value?: string, field?: string, length?: number) => {
        let error = validateFields(value, field, length);

        const configIdError = checkConfigIdErrors(assessmentValues, assessments || [], value);
        if (field === 'configId') {
            error = { configId: configIdError };
        }

        setErrors({
            ...errors,
            ...error,
        });
    };

    const onChange = (fieldName: string) => (event: any) => {
        const { value } = event.target;
        const length = fieldName === 'configId' ? 24 : 250;
        disableBtns.setFalse();
        setAssessmentValues({
            ...assessmentValues,
            [fieldName]: value,
        });
        validate(value, fieldName, length);
    };

    const onCheckboxChange = (fieldName: 'active' | 'endPoint' | 'safety' | 'lab' | 'sendToVendor') => () => {
        disableBtns.setFalse();
        setAssessmentValues({ ...assessmentValues, [fieldName]: !assessmentValues[fieldName] });
    };

    const rulesValidation = () => addAssessmentState.findIndex((item) => !item.isValid) === -1;

    const fieldExistError = () => {
        const isNameExist = checkIfNameExist(
            { ...assessmentValues, name: assessmentValues.name?.trim(), id: null },
            assessments,
            'name',
        );

        if (
            (isNameExist && currentAssessment === null) ||
            (currentAssessment && currentAssessment.name !== assessmentValues.name && isNameExist)
        ) {
            setErrors({ ...errors, name: t('admin:assessmentNameAlreadyExists') });
            return true;
        }

        return false;
    };

    const validateCrfFormId = (value?: string) => {
        if (crfConvert('singleSubjectCode', value) === '') {
            setErrors({
                ...errors,
                crfId: t('admin:saveAssessmentInvalidCrfFormID'),
            });

            return false;
        }
        return true;
    };

    const submit = () => {
        disableBtns.setTrue();
        const requiredFields = getRequiredFields();

        if (!rulesValidation()) {
            showRulesValidation.setTrue();
            return;
        }

        if (currentAssessment) {
            const configIdError = checkConfigIdErrors(assessmentValues, assessments || []);

            if (configIdError) {
                setErrors({
                    ...errors,
                    configId: configIdError,
                });
                return;
            }
        }

        if (
            checkRequiredFields({
                fields: requiredFields,
                state: assessmentValues,
                setErrors,
                errors,
            }) &&
            validateCrfFormId(assessmentValues.crfId) &&
            !fieldExistError()
        ) {
            saveAssessment({
                variables: {
                    studyId,
                    assessment: {
                        id: currentAssessment?.id || null,
                        configId: assessmentValues.configId,
                        crfId: assessmentValues.crfId,
                        active: assessmentValues.active,
                        name: assessmentValues.name,
                        endPoint: assessmentValues.endPoint,
                        safety: assessmentValues.safety,
                        lab: assessmentValues.lab,
                        sendToVendor: assessmentValues.sendToVendor,
                        assessmentRules: addAssessmentState
                            .filter((rule: SVTRule) => rule.field !== ADD_RULES_FIELD_TYPES.default)
                            .map((rule: SVTRule) => ({
                                comparatorField: rule.field,
                                ruleClauseOperator: rule.operator,
                                value: rule.value,
                                ruleClause: rule.andOr,
                            })),
                    },
                },
                refetchQueries: [
                    {
                        query: GET_STUDY_CRF_ASSESSMENTS,
                        variables: { studyId: studyId ?? null },
                    },
                ],
            })
                .then(() => {
                    onClose(currentAssessment ? 'edit' : 'success');
                })
                .catch(() => {
                    onClose('error');
                });
        }
    };

    const onChangeStep = (number: number) => {
        if (isSysAdmin) return;

        const requiredFields = getRequiredFields();

        if (currentAssessment) {
            const configIdError = checkConfigIdErrors(assessmentValues, assessments || []);

            if (configIdError) {
                setErrors({
                    ...errors,
                    configId: configIdError,
                });
                return;
            }
        }

        if (
            checkRequiredFields({
                fields: requiredFields,
                state: assessmentValues,
                setErrors,
                errors,
            }) &&
            rulesValidation() &&
            validateCrfFormId(assessmentValues.crfId) &&
            !fieldExistError()
        ) {
            setTab({ number, available: tabNumber.step2Rules });
        }
    };

    const backToAssessment = () => {
        showRulesValidation.setTrue();
        if (isBackBtnDisabled) {
            disableBtns.setTrue();
        } else {
            onChangeStep(1);
        }
    };

    useEffect(() => {
        if (rulesValidation() && disableBtns.value) {
            disableBtns.setFalse();
        }
    }, [addAssessmentState]);

    return (
        <CustomDialog
            onClose={() => {
                onClose();
            }}
            fullWidth
            maxWidth="md"
            scroll="paper"
            open
            className="custom-width-popup assessment-modal max-w700"
        >
            <DialogTitle id="add-crf-modal-title">
                {currentAssessment
                    ? t('admin:editAssessmentNumber', { recordId: currentAssessment.recordId })
                    : t('admin:addAssessment')}
                <FontAwesomeIcon
                    icon={['fal', 'times']}
                    className="fa-lg close-icon-cv"
                    onClick={() => {
                        onClose();
                    }}
                    data-testid="close-dialog"
                />
            </DialogTitle>
            <DialogContent className="assessment-dialog-content">
                <StepsBox page={tab} onChangeStep={onChangeStep} pageTitles={tabTitles} />

                {tab.number === tabNumber.step1Assessments && (
                    <div>
                        <div className="checkboxes-wrapper mb-10">
                            <div className="left-part">
                                <Checkbox
                                    id="assessment-active-checkbox"
                                    className="cell-checkbox no-border mr"
                                    checked={assessmentValues.active}
                                    onChange={onCheckboxChange('active')}
                                    label={t<string>('admin:active')}
                                    rounded={null}
                                    disabled={isSysAdmin}
                                    data-testid="assessment-active-checkbox"
                                    size="medium"
                                />
                            </div>
                        </div>
                        <div className="type-edc-wrapper">
                            <CustomAutocomplete
                                label={`${t('admin:crfFormId')}${isFormIdRequired.value ? '*' : ''}`}
                                placeholder={t('admin:enterFormId')}
                                className="mr-20"
                                options={crfFormIdList}
                                disabled={!isFormIdRequired.value || isSysAdmin}
                                value={crfConvert('singleSubjectCode', assessmentValues.crfId)}
                                onChange={(value: string) => {
                                    setAssessmentValues({
                                        ...assessmentValues,
                                        crfId: crfConvert('crfId', value),
                                    });
                                    disableBtns.setFalse();

                                    if (isFormIdRequired.value) validate(value, 'crfId');
                                }}
                                error={errors.crfId}
                            />
                            <TextField
                                label={t('common:name')}
                                id="add-assessment-name"
                                fullWidth
                                required
                                value={assessmentValues.name}
                                onChange={onChange('name')}
                                placeholder={t('admin:enterName')}
                                helperText={
                                    <CharactersRemaining
                                        id="add-assessment-name-dialog-help-text"
                                        length={assessmentValues.name?.length}
                                        max={250}
                                        error={errors.name}
                                    />
                                }
                                variant="outlined"
                                color="secondary"
                                error={!!errors.name}
                                className="before-select"
                                disabled={isSysAdmin}
                            />
                        </div>
                        <div className={cn('checkboxes-wrapper', { 'mb-20': currentAssessment })}>
                            <div className="left-part">
                                <Checkbox
                                    id="assessment-end-point-checkbox"
                                    className="cell-checkbox no-border mr"
                                    checked={assessmentValues.endPoint}
                                    onChange={onCheckboxChange('endPoint')}
                                    label={t<string>('admin:endPoint')}
                                    rounded={null}
                                    disabled={isSysAdmin}
                                    data-testid="assessment-end-point-checkbox"
                                    size="medium"
                                />
                                <Checkbox
                                    id="assessment-safety-checkbox"
                                    className="cell-checkbox no-border mr"
                                    checked={assessmentValues.safety}
                                    onChange={onCheckboxChange('safety')}
                                    label={t<string>('admin:safety')}
                                    rounded={null}
                                    disabled={isSysAdmin}
                                    data-testid="assessment-safety-checkbox"
                                    size="medium"
                                />
                                <Checkbox
                                    id="assessment-lab-checkbox"
                                    className="cell-checkbox no-border mr"
                                    checked={assessmentValues.lab}
                                    onChange={onCheckboxChange('lab')}
                                    label={t<string>('admin:lab')}
                                    rounded={null}
                                    disabled={isSysAdmin}
                                    data-testid="assessment-lab-checkbox"
                                    size="medium"
                                />
                                <Checkbox
                                    id="assessment-send-vendor-checkbox"
                                    className="cell-checkbox no-border mr"
                                    checked={assessmentValues.sendToVendor}
                                    onChange={onCheckboxChange('sendToVendor')}
                                    label={t<string>('admin:sendToVendor')}
                                    rounded={null}
                                    disabled={isSysAdmin}
                                    data-testid="assessment-send-vendor-checkbox"
                                    size="medium"
                                />
                            </div>
                        </div>
                        {!!currentAssessment && (
                            <div className="type-edc-wrapper no-mb">
                                <FormControl variant="outlined" className="form-control-type">
                                    <TextField
                                        readOnly={!isSysAdmin}
                                        id="edit-field-config-id"
                                        label={t('admin:configId')}
                                        onChange={onChange('configId')}
                                        fullWidth
                                        value={assessmentValues.configId}
                                        size="small"
                                        InputProps={{
                                            readOnly: !isSysAdmin,
                                            endAdornment: <CopyButton value={assessmentValues.configId ?? ''} />,
                                        }}
                                        helperText={
                                            <span style={{ color: '#ae0015' }} className="remaining-text">
                                                {errors.configId}
                                            </span>
                                        }
                                        variant="outlined"
                                        color="secondary"
                                        error={!!errors.configId}
                                    />
                                </FormControl>
                            </div>
                        )}
                    </div>
                )}
                <AddRules
                    formState={addAssessmentState}
                    setFormState={setAddAssessmentState}
                    studyId={studyId}
                    hidden={tab.number !== tabNumber.step2Rules}
                    showValidation={showRulesValidation.value}
                />
            </DialogContent>
            <DialogActions
                className={cn('dialog-content-actions', { 'flex-end': tab.number < 2 && !currentAssessment })}
            >
                {tab.number === tabNumber.step1Assessments && <ModalUpdatedInfo item={currentAssessment} />}
                {tab.number === tabNumber.step2Rules && (
                    <Button
                        text="admin:backToAssessmentButton"
                        id="back-button-index"
                        className="mr-10"
                        disabled={
                            disableBtns.value ||
                            ((showRulesValidation.value || isForcedValidation) && isBackBtnDisabled)
                        }
                        onClick={backToAssessment}
                    />
                )}
                <div>
                    <Button
                        id="submit-assessment-btn"
                        disabled={
                            !!Object.keys(errors).filter((item) => errors[item]).length ||
                            disableBtns.value ||
                            ((showRulesValidation.value || isForcedValidation) && isBackBtnDisabled)
                        }
                        text={currentAssessment ? 'common:update' : 'common:save'}
                        onClick={submit}
                    />

                    {tab.number === tabNumber.step1Assessments && (
                        <Button
                            text="common:nextButton"
                            id="next-button-index"
                            className="ml-10"
                            disabled={
                                !!Object.keys(errors).filter((item) => errors[item]).length ||
                                disableBtns.value ||
                                isSysAdmin
                            }
                            onClick={() => {
                                if (!fieldExistError()) {
                                    onChangeStep(2);
                                }
                            }}
                        />
                    )}
                </div>
            </DialogActions>
        </CustomDialog>
    );
};

export default AssessmentDialog;
